import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Zap, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

export default function Register() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [googleEmail, setGoogleEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/register", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        google_email: googleEmail.trim().toLowerCase(),
      });
      setDone(true);
      toast.success("Request received — you'll be notified when approved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6">
        <Card className="w-full max-w-md p-8 border-zinc-200 text-center" data-testid="register-success">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h1 className="mt-4 font-display font-black text-2xl text-slate-900">Request received</h1>
          <p className="mt-2 text-slate-600 text-sm">
            An administrator will review your request. You&apos;ll be able to sign in once your account is activated.
          </p>
          <Button className="mt-6 w-full bg-zinc-900 hover:bg-zinc-800 text-white" onClick={() => navigate("/login")} data-testid="back-to-login">
            Back to sign in
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative">
        <img
          alt=""
          src="https://images.unsplash.com/photo-1550029402-226115b7c579?crop=entropy&cs=tinysrgb&fm=jpg&q=80&w=1600"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/30 via-transparent to-cyan-400/30 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute top-6 left-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-amber-950 px-2.5 py-1 text-[11px] font-bold tracking-wide">BETA · TEST USERS ONLY</span>
        </div>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-display font-black text-3xl sm:text-4xl drop-shadow-lg">Request access</div>
          <p className="mt-3 max-w-md drop-shadow">Every account is approved by an admin during the beta.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8 relative">
        <div className="absolute top-4 right-4 lg:hidden">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-amber-950 px-2.5 py-1 text-[10px] font-bold tracking-wide">BETA</span>
        </div>
        <Card className="w-full max-w-md p-6 sm:p-8 border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-cyan-500 text-white grid place-items-center">
              <Zap className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display font-black text-lg text-slate-900 tracking-tight">PostQik</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Create account</div>
            </div>
          </div>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-slate-700">Full name</Label>
              <Input id="name" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" autoComplete="name" data-testid="reg-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-700">Sign-in email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" data-testid="reg-email-input" />
            </div>
            <div>
              <Label htmlFor="google" className="text-slate-700">Google Drive email</Label>
              <Input id="google" type="email" required value={googleEmail} onChange={(e) => setGoogleEmail(e.target.value)} placeholder="you@gmail.com" data-testid="reg-google-input" />
              <p className="mt-1 text-xs text-slate-500">The Google account whose Drive you want to connect. Needed for pre-approval.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pw" className="text-slate-700">Password</Label>
                <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} autoComplete="new-password" data-testid="reg-password-input" />
              </div>
              <div>
                <Label htmlFor="pw2" className="text-slate-700">Confirm</Label>
                <Input id="pw2" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={8} autoComplete="new-password" data-testid="reg-confirm-input" />
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-fuchsia-600 to-cyan-600 hover:from-fuchsia-700 hover:to-cyan-700 text-white h-11" data-testid="reg-submit-button">
              {submitting ? "Sending…" : "Request access"}
            </Button>
          </form>
          <div className="mt-5 text-center text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-fuchsia-700 hover:underline" data-testid="signin-link">Sign in</Link>
          </div>
          <div className="mt-6 pt-4 border-t border-zinc-100 flex items-center justify-center gap-4 text-[11px] text-slate-500">
            <Link to="/privacy" className="hover:text-slate-900" data-testid="reg-privacy-link">
              Privacy Policy
            </Link>
            <span aria-hidden="true">•</span>
            <Link to="/tos" className="hover:text-slate-900" data-testid="reg-tos-link">
              Terms of Service
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
